/**
 * useTasks — fetches real FHIR Task resources from Medplum.
 *
 * Replaces the previous `useState(mockTasks)` pattern.
 * Provides loading / error / refresh lifecycle and optimistic helpers.
 */

import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Task } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import { enrichTasksWithPatientDisplay } from '../../../lib/enrichTasksWithPatientDisplay';
import { searchTasks } from '../services/taskService';

export interface UseTasksResult {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Replace a single task in local state (optimistic). */
  optimisticUpdate: (updated: Task) => void;
  /** Remove a task from local state (optimistic). */
  optimisticRemove: (taskId: string) => void;
  /** Prepend a task to local state (optimistic). */
  optimisticAdd: (task: Task) => void;
  /** Replace the full local task list (e.g. after bulk op rollback). */
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
}

export function useTasks(): UseTasksResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const results = await searchTasks(medplum);
        const enriched = await enrichTasksWithPatientDisplay(medplum, results);
        if (!cancelled) setTasks(enriched);
      } catch (err) {
        if (!cancelled) {
          console.error('[useTasks] Failed to fetch tasks:', err);
          setError(err instanceof Error ? err.message : 'Failed to load tasks');
          setTasks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [medplum, profile?.id, profile?.resourceType, tick]);

  const optimisticUpdate = useCallback((updated: Task) => {
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  }, []);

  const optimisticRemove = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  const optimisticAdd = useCallback((task: Task) => {
    setTasks((prev) => [task, ...prev]);
  }, []);

  return {
    tasks,
    loading,
    error,
    refresh,
    optimisticUpdate,
    optimisticRemove,
    optimisticAdd,
    setTasks,
  };
}
